import { useCallback, useMemo } from 'react';
import { WorkingDaysType } from '../contexts/types';
import { useSpreadSheetSync, SpreadSheetSyncConfig } from './useSpreadSheetSync';
import GoogleAPI from '../utils/GoogleAPI';
import i18n from '../utils/i18n';

const T_PREFIX = 'features.InvestmentScreening.WorkingDays.spreadsheet';

/**
 * 稼働日数データの年度ごとの型
 */
export interface WorkingDaysYearData {
  fiscalYear: string;
  workingDaysData: WorkingDaysType;
}

/**
 * 稼働日数データ変換結果
 */
export interface WorkingDaysConversionResult {
  data: WorkingDaysYearData[];
  errors: string[];
}

/** 月名配列（1月始まり・日本語） */
const MONTH_NAMES_JA = ["1月", "2月", "3月", "4月", "5月", "6月", "7月", "8月", "9月", "10月", "11月", "12月"];

/** 月名配列（1月始まり・英語） */
const MONTH_NAMES_EN = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * 現在の言語設定（i18n.language）に応じた月名配列（1月始まり）を取得する。
 * 言語切替時にも正しい表示になるよう、都度評価する関数として提供する。
 */
export const getMonthNames = (): string[] => (i18n.language === "en" ? MONTH_NAMES_EN : MONTH_NAMES_JA);

/**
 * 現在の言語設定（i18n.language）に応じた月名配列（4月始まり）を取得する。
 */
export const getMonthNamesAprilStart = (): string[] => shiftToAprilStart(getMonthNames());

/** @deprecated 言語切替に追従しないため getMonthNames() を使用してください */
export const MONTH_NAMES = MONTH_NAMES_JA;

/** @deprecated 言語切替に追従しないため getMonthNamesAprilStart() を使用してください */
export const MONTH_NAMES_APRIL_START = MONTH_NAMES_JA.length ? [...MONTH_NAMES_JA.slice(3), ...MONTH_NAMES_JA.slice(0, 3)] : [];

/** 各月の日数 (1月〜12月順) */
export const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/**
 * 1月〜12月順 → 4月〜3月順にシフト
 */
export const shiftToAprilStart = <T,>(arr: T[]): T[] => {
  return [...arr.slice(3), ...arr.slice(0, 3)];
};

/**
 * 4月〜3月順 → 1月〜12月順にシフト
 */
export const shiftToJanuaryStart = <T,>(arr: T[]): T[] => {
  if (!arr || arr.length < 12) {
    const padded = [...(arr || []), ...Array(12 - (arr?.length || 0)).fill("" as unknown as T)];
    return [...padded.slice(-3), ...padded.slice(0, 9)];
  }
  return [...arr.slice(-3), ...arr.slice(0, 9)];
};

/**
 * 稼働日数データをSpreadSheetデータに変換
 */
export const workingDaysToSpreadSheetData = (data: WorkingDaysYearData[]): any[][] => {
  return data.flatMap(yearData => {
    const year = yearData.fiscalYear ?? "";
    const factoryWorkingDaysArray = Object.values(yearData.workingDaysData.factoryWorkingDays ?? {}).length > 0
      ? Object.values(yearData.workingDaysData.factoryWorkingDays!)
      : Array(12).fill(0);
    const workingDaysArray = Object.values(yearData.workingDaysData.workingDays);
    const weekendWorkDaysArray = Object.values(yearData.workingDaysData.workOnWeekendAndHolidays ?? {}).length > 0
      ? Object.values(yearData.workingDaysData.workOnWeekendAndHolidays!)
      : Array(12).fill(0);
    const holidayWorkDaysArray = Object.values(yearData.workingDaysData.workOnHolidays ?? {}).length > 0
      ? Object.values(yearData.workingDaysData.workOnHolidays!)
      : Array(12).fill(0);
    const lineCountArray = Object.values(yearData.workingDaysData.lineCount ?? {}).length > 0
      ? Object.values(yearData.workingDaysData.lineCount!)
      : Array(12).fill(1);

    return [
      [year, ...getMonthNamesAprilStart()],
      [i18n.t(`${T_PREFIX}.rowLabel.factoryWorkingDays`), ...shiftToAprilStart(factoryWorkingDaysArray)],
      [i18n.t(`${T_PREFIX}.rowLabel.innerWorkingDays`), ...shiftToAprilStart(workingDaysArray)],
      [i18n.t(`${T_PREFIX}.rowLabel.holidayWorkDays`), ...shiftToAprilStart(weekendWorkDaysArray)],
      [i18n.t(`${T_PREFIX}.rowLabel.holidayWork`), ...shiftToAprilStart(holidayWorkDaysArray)],
      [i18n.t(`${T_PREFIX}.rowLabel.activeLines`), ...shiftToAprilStart(lineCountArray)],
      []
    ];
  });
};

/**
 * SpreadSheetデータを稼働日数データに変換
 */
export const spreadSheetDataToWorkingDays = (
  values: any[][],
  existingData: WorkingDaysYearData[]
): WorkingDaysConversionResult => {
  const invalidValueErrors: string[] = [];
  const ROWS_PER_YEAR = 7;

  // データが足りない分空配列を追加
  if (existingData.length * ROWS_PER_YEAR > values.length) {
    const minus = existingData.length * ROWS_PER_YEAR - values.length;
    for (let i = 0; i < minus; i++) {
      values.push([]);
    }
  }

  // 数値変換＋月シフト
  const convertToNumberAndShift = (arr: any[][], fieldName: string, defaultValue: number): number[][] => {
    return arr.map((inner, yearIndex) => {
      const fiscalYear = existingData[yearIndex]?.fiscalYear || `${yearIndex + 1}年目`;
      let converted = inner.map((item, monthIndex) => {
        if (item === null || item === undefined || item === "") {
          return defaultValue;
        }
        const num = Number(item);
        if (isNaN(num) || num < 0) {
          invalidValueErrors.push(i18n.t(`${T_PREFIX}.message.invalidValueSetToZero`, {
            location: `${fiscalYear} ${getMonthNamesAprilStart()[monthIndex]}`,
            fieldName,
            value: item,
            defaultValue,
          }));
          return defaultValue;
        }
        return num;
      });
      if (converted.length < 12) {
        converted = [...converted, ...Array(12 - converted.length).fill(defaultValue)];
      }
      return shiftToJanuaryStart(converted);
    });
  };

  // 各項目取得
  const factoryWorkingDaysArray = convertToNumberAndShift(values.filter((_, index) => (index + 1) % ROWS_PER_YEAR === 2), "工場稼働日数", 0);
  const workingDaysArray = convertToNumberAndShift(values.filter((_, index) => (index + 1) % ROWS_PER_YEAR === 3), "(内)稼働日数", 0);
  const weekendWorkDaysArray = convertToNumberAndShift(values.filter((_, index) => (index + 1) % ROWS_PER_YEAR === 4), "休日稼働日数", 0);
  const holidayWorkDaysArray = convertToNumberAndShift(values.filter((_, index) => (index + 1) % ROWS_PER_YEAR === 5), "(内)祝日出勤", 0);
  const lineCountArray = convertToNumberAndShift(values.filter((_, index) => (index + 1) % ROWS_PER_YEAR === 6), "稼働ライン数", 1);

  // バリデーション：稼働日数
  const validateWorkingDays = (arr: number[][]): number[][] => {
    return arr.map((inner, yearIndex) => {
      const fiscalYear = existingData[yearIndex]?.fiscalYear || `${yearIndex + 1}年目`;
      return inner.map((value, monthIndex) => {
        const rounded = Math.floor(value);
        const max = DAYS_IN_MONTH[monthIndex];
        const isValid = rounded >= 0 && rounded <= max;
        if (!isValid && value !== 0) {
          invalidValueErrors.push(i18n.t(`${T_PREFIX}.message.invalidValueSetToZero`, {
            location: `${fiscalYear} ${getMonthNames()[monthIndex]}`,
            fieldName: i18n.t(`${T_PREFIX}.rowLabel.innerWorkingDays`),
            value,
            defaultValue: 0,
          }));
        }
        return isValid ? rounded : 0;
      });
    });
  };

  // バリデーション：ライン数
  const validateLineCount = (arr: number[][]): number[][] => {
    return arr.map((inner, yearIndex) => {
      const fiscalYear = existingData[yearIndex]?.fiscalYear || `${yearIndex + 1}年目`;
      return inner.map((v, monthIndex) => {
        // 共用ラインを割合で表すため小数第2位まで許可（画面入力と同じ仕様）
        const rounded = Math.round(v * 100) / 100;
        const isValid = rounded >= 0 && rounded <= 99;
        if (!isValid && v !== 0) {
          invalidValueErrors.push(i18n.t(`${T_PREFIX}.message.invalidValueSetToZero`, {
            location: `${fiscalYear} ${getMonthNames()[monthIndex]}`,
            fieldName: i18n.t(`${T_PREFIX}.rowLabel.activeLines`),
            value: v,
            defaultValue: 1,
          }));
        }
        return isValid ? rounded : 1;
      });
    });
  };

  // バリデーション：工場稼働日数
  const validateFactoryWorkingDays = (arr: number[][]): number[][] => {
    return arr.map((inner, yearIndex) => {
      const fiscalYear = existingData[yearIndex]?.fiscalYear || `${yearIndex + 1}年目`;
      return inner.map((value, monthIndex) => {
        const rounded = Math.floor(value);
        const max = DAYS_IN_MONTH[monthIndex];
        const isValid = rounded >= 0 && rounded <= max;
        if (!isValid && value !== 0) {
          invalidValueErrors.push(i18n.t(`${T_PREFIX}.message.invalidValueSetToZero`, {
            location: `${fiscalYear} ${getMonthNames()[monthIndex]}`,
            fieldName: i18n.t(`${T_PREFIX}.rowLabel.factoryWorkingDays`),
            value,
            defaultValue: 0,
          }));
        }
        return isValid ? rounded : 0;
      });
    });
  };

  // バリデーション：休日出勤/祝日出勤
  const validateWeekendHolidayWorkDays = (arr: number[][], fieldName: string): number[][] => {
    return arr.map((inner, yearIndex) => {
      const fiscalYear = existingData[yearIndex]?.fiscalYear || `${yearIndex + 1}年目`;
      return inner.map((value, monthIndex) => {
        const rounded = Math.floor(value);
        const max = DAYS_IN_MONTH[monthIndex];
        const isValid = rounded >= 0 && rounded <= max;
        if (!isValid && value !== 0) {
          invalidValueErrors.push(i18n.t(`${T_PREFIX}.message.invalidValueSetToZero`, {
            location: `${fiscalYear} ${getMonthNames()[monthIndex]}`,
            fieldName,
            value,
            defaultValue: 0,
          }));
        }
        return isValid ? rounded : 0;
      });
    });
  };

  const validFactoryWorkingDaysArray = validateFactoryWorkingDays(factoryWorkingDaysArray);
  const validWorkingDaysArray = validateWorkingDays(workingDaysArray);
  const validWeekendWorkDaysArray = validateWeekendHolidayWorkDays(weekendWorkDaysArray, "休日稼働日数");
  const validHolidayWorkDaysArray = validateWeekendHolidayWorkDays(holidayWorkDaysArray, "(内)祝日出勤");
  const validLineCountArray = validateLineCount(lineCountArray);

  // 関連性チェック0: (内)稼働日数 ≤ 工場稼働日数
  validWorkingDaysArray.forEach((yearWorkingDays, yearIndex) => {
    const fiscalYear = existingData[yearIndex]?.fiscalYear || `${yearIndex + 1}年目`;
    yearWorkingDays.forEach((workingDay, monthIndex) => {
      const factoryWorkingDay = validFactoryWorkingDaysArray[yearIndex]?.[monthIndex] ?? 0;
      if (factoryWorkingDay > 0 && workingDay > factoryWorkingDay) {
        validWorkingDaysArray[yearIndex][monthIndex] = factoryWorkingDay;
        invalidValueErrors.push(
          i18n.t(`${T_PREFIX}.message.workingDaysExceedFactory`, {
            location: `${fiscalYear} ${getMonthNames()[monthIndex]}`,
            days: factoryWorkingDay,
          })
        );
      }
    });
  });

  // 関連性チェック1: (内)稼働日数 + 休日稼働日数 ≤ 月の日数
  validWorkingDaysArray.forEach((yearWorkingDays, yearIndex) => {
    const fiscalYear = existingData[yearIndex]?.fiscalYear || `${yearIndex + 1}年目`;
    yearWorkingDays.forEach((workingDay, monthIndex) => {
      const weekendWorkDay = validWeekendWorkDaysArray[yearIndex]?.[monthIndex] ?? 0;
      const maxDays = DAYS_IN_MONTH[monthIndex];
      const sumDays = workingDay + weekendWorkDay;
      if (sumDays > maxDays) {
        const adjustedWeekendWorkDay = Math.max(0, maxDays - workingDay);
        validWeekendWorkDaysArray[yearIndex][monthIndex] = adjustedWeekendWorkDay;
        invalidValueErrors.push(
          i18n.t(`${T_PREFIX}.message.workingDaysAndHolidayExceedMax`, {
            location: `${fiscalYear} ${getMonthNames()[monthIndex]}`,
            maxDays,
            days: adjustedWeekendWorkDay,
          })
        );
      }
    });
  });

  // 関連性チェック2: (内)祝日出勤 ≤ 休日稼働日数
  validWeekendWorkDaysArray.forEach((yearWeekendWorkDays, yearIndex) => {
    const fiscalYear = existingData[yearIndex]?.fiscalYear || `${yearIndex + 1}年目`;
    yearWeekendWorkDays.forEach((weekendWorkDay, monthIndex) => {
      const holidayWorkDay = validHolidayWorkDaysArray[yearIndex]?.[monthIndex] ?? 0;
      if (holidayWorkDay > weekendWorkDay) {
        validHolidayWorkDaysArray[yearIndex][monthIndex] = weekendWorkDay;
        invalidValueErrors.push(
          i18n.t(`${T_PREFIX}.message.holidayWorkExceedWeekend`, {
            location: `${fiscalYear} ${getMonthNames()[monthIndex]}`,
            days: weekendWorkDay,
          })
        );
      }
    });
  });

  // オブジェクト作成
  const convertedData: WorkingDaysYearData[] = existingData.map((yearData, i) => ({
    ...yearData,
    workingDaysData: {
      ...yearData.workingDaysData,
      factoryWorkingDays: validFactoryWorkingDaysArray[i] ?? [],
      workingDays: validWorkingDaysArray[i] ?? [],
      workOnWeekendAndHolidays: validWeekendWorkDaysArray[i] ?? [],
      workOnHolidays: validHolidayWorkDaysArray[i] ?? [],
      lineCount: validLineCountArray[i] ?? [],
      sumFactoryWorkingDays: validFactoryWorkingDaysArray[i]?.reduce((acc, n) => acc + n, 0) ?? 0,
      sumWorkingDays: validWorkingDaysArray[i]?.reduce((acc, n) => acc + n, 0) ?? 0,
      sumWeekendWorkDays: validWeekendWorkDaysArray[i]?.reduce((acc, n) => acc + n, 0) ?? 0,
      sumHolidayWorkDays: validHolidayWorkDaysArray[i]?.reduce((acc, n) => acc + n, 0) ?? 0,
    }
  }));

  return { data: convertedData, errors: invalidValueErrors };
};

/**
 * 稼働日数SpreadSheet同期用カスタムフック
 */
export const useWorkingDaysSpreadSheet = (
  googleAPI: GoogleAPI,
  toast: (options: { title: string; description: React.ReactNode; status: 'error' | 'warning' | 'success' | 'info'; duration: number; isClosable: boolean }) => void,
  existingData: WorkingDaysYearData[]
) => {
  const config = useMemo<SpreadSheetSyncConfig<WorkingDaysYearData[]>>(() => ({
    folderPath: "CEAS/SpreadSheet/WorkingDays",
    fileName: i18n.t(`${T_PREFIX}.fileName`),
    sharedDriveName: null,
    toSpreadSheetData: workingDaysToSpreadSheetData,
    fromSpreadSheetData: (values) => spreadSheetDataToWorkingDays(values, existingData).data,
    getRange: (sheetName) => `${sheetName}!B1:M`,
  }), [existingData]);

  const syncResult = useSpreadSheetSync(config, googleAPI, toast);

  // データ連携時にエラーも取得できるようにラップ
  const handleSyncDataWithErrors = useCallback(async (): Promise<WorkingDaysConversionResult | null> => {
    const rawResult = await syncResult.handleSyncData();
    if (!rawResult) return null;

    // 再度変換してエラーを取得（handleSyncDataはデータのみ返すため）
    // Note: 実際には変換済みデータを返すので、ここでの再変換は不要
    // エラー取得のために一度rawデータにアクセスする必要がある場合は、
    // カスタムフックを拡張する
    return { data: rawResult, errors: [] };
  }, [syncResult]);

  return {
    ...syncResult,
    handleSyncDataWithErrors,
  };
};

export default useWorkingDaysSpreadSheet;
